import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { COMPANY } from "../../lib/api";
import { SERVICES } from "../../data/site";
import { MagneticButton } from "./MagneticButton";
import { BookingCTA } from "./BookingCTA";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Property & Strata", to: "/property-strata-managers" },
  { label: "Our Work", to: "/our-work" },
  { label: "About", to: "/about" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-black/5 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img src={scrolled ? "/logo-full-dark.png" : "/logo-full-white.png"} alt="West Face Roofing & Projects" className="h-9 md:h-11 w-auto transition-transform group-hover:scale-[1.03]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) =>
            n.label === "Services" ? (
              <div key={n.to} className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
                <Link to={n.to} data-testid="nav-services" className={`px-3.5 py-2 rounded-full text-sm font-semibold flex items-center gap-1 transition-colors ${scrolled ? "text-[#1A2421] hover:bg-black/5" : "text-white/90 hover:bg-white/10"}`}>
                  Services <ChevronDown size={14} />
                </Link>
                <AnimatePresence>
                  {svcOpen && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      className="absolute top-full left-0 pt-3">
                      <div className="w-[320px] bg-white rounded-2xl shadow-2xl border border-black/5 p-2">
                        {SERVICES.map((s) => (
                          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-svc-${s.slug}`}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F1F5F0] transition-colors">
                            <s.icon size={18} className="text-[#62B848] shrink-0" />
                            <span className="text-sm font-medium text-[#1A2421]">{s.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${scrolled ? "text-[#1A2421] hover:bg-black/5" : "text-white/90 hover:bg-white/10"}`}>
                {n.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a href={COMPANY.phoneHref} data-testid="header-call-btn"
            className={`hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${scrolled ? "text-[#224733] hover:bg-[#224733]/5" : "text-white hover:bg-white/10"}`}>
            <Phone size={16} /> {COMPANY.phone}
          </a>
          <BookingCTA testId="header-quote-btn"
            className="hidden sm:inline-flex items-center bg-[#62B848] hover:bg-[#4E9B38] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer">
            Get a free quote
          </BookingCTA>
          <button data-testid="mobile-menu-btn" onClick={() => setMobile((v) => !v)}
            className={`lg:hidden grid place-items-center w-10 h-10 rounded-full ${scrolled ? "text-[#1A2421] bg-black/5" : "text-white bg-white/15"}`}>
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-black/5">
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className="px-3 py-3 rounded-xl font-semibold text-[#1A2421] hover:bg-[#F1F5F0]">
                  {n.label}
                </Link>
              ))}
              <a href={COMPANY.phoneHref} className="mt-2 flex items-center justify-center gap-2 bg-[#224733] text-white font-bold px-4 py-3 rounded-full">
                <Phone size={16} /> Call {COMPANY.phone}
              </a>
              <BookingCTA testId="mnav-quote-btn"
                className="flex items-center justify-center gap-2 bg-[#62B848] text-white font-bold px-4 py-3 rounded-full cursor-pointer">
                Get a free quote
              </BookingCTA>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
